import React, { useState } from "react";
import PropTypes from "prop-types";
import { Popconfirm, Tooltip, Button, Flex, Input } from "antd";
import {
  PersonSearchButton,
  GroupSearchButton,
} from "@authzsvc/common-react-components";
import {
  HIDE_DELETE_BUTTON,
  HIDE_CHANGE_ADMINISTRATOR_GROUP_BUTTON,
  ResourceStates,
} from "../../definitions";
import {
  DeleteOutlined,
  SettingOutlined,
  UserSwitchOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { format } from "../common/utils";

const DeleteButtonComponent = ({ onDelete, item }) => {
  const shouldHideDeleteButton =
    !item.managedResourceType ||
    HIDE_DELETE_BUTTON.Resources.includes(item.managedResourceType);
  const [confirmed, setConfirmed] = useState(false);

  return (
    <Popconfirm
      placement="bottomRight"
      okButtonProps={{ disabled: !confirmed }}
      title="Are you sure you want to delete this resource?"
      description={
        <>
          {item.homePage && (
            <p>
              Resource homepage is:
              <a href={item.homePage} target="_blank" rel="noreferrer">
                {item.homePage}
              </a>
            </p>
          )}
          Deleting resource <b>{item.displayName}</b> might have strong
          implications,
          <br />
          please confirm deletion by typing <b>{item.displayName}</b> in the
          form below:
          <br />
          <Input
            placeholder="Type resource name"
            style={{ width: "400px" }}
            onChange={(e) => {
              if (e.target.value === item.displayName) setConfirmed(true);
              else setConfirmed(false);
            }}
          />
        </>
      }
      onConfirm={() => onDelete(item)}
    >
      <Tooltip
        title={
          shouldHideDeleteButton
            ? "This resource cannot be deleted from this portal, click manage button for more options"
            : "Delete this resource"
        }
      >
        <Button
          danger
          disabled={shouldHideDeleteButton}
          icon={<DeleteOutlined />}
        />
      </Tooltip>
    </Popconfirm>
  );
};

DeleteButtonComponent.propTypes = {
  onDelete: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
};

const ApproveComponent = ({ onApprove, item }) => {
  return (
    <Tooltip title="Become the owner of this resource">
      <Button
        onClick={function () {
          onApprove(item);
        }}
      >
        Take Ownership
      </Button>
    </Tooltip>
  );
};

ApproveComponent.propTypes = {
  onApprove: PropTypes.func.isRequired,
  item: PropTypes.shape({
    internalState: PropTypes.string.isRequired,
  }).isRequired,
};

const ResourceActionButtons = ({
  onDelete,
  onApprove,
  onEditOwner,
  onSelectNewAdminGroup,
  item,
  managementUrlTemplate,
}) => {
  const selectNewOwner = (owner) => {
    onEditOwner(item, owner);
  };
  const selectNewAdminGroup = (group) => {
    onSelectNewAdminGroup(item, group);
  };
  const reassignComponent = (
    <PersonSearchButton
      title="Transfer this resource to someone else"
      onSelect={selectNewOwner}
      icon={<UserSwitchOutlined />}
    />
  );

  const ChangeAdminGroupComponent = ({ item }) => {
    if (item.resourceCategory === "Personal")
      return (
        <Tooltip title="Personal resources cannot be managed by a group">
          <Button disabled icon={<UsergroupAddOutlined />} />
        </Tooltip>
      );
    if (
      HIDE_CHANGE_ADMINISTRATOR_GROUP_BUTTON.Resources.includes(
        item.managedResourceType,
      )
    )
      return (
        <Tooltip title="Administrator group cannot be changed through this portal.">
          <Button disabled icon={<UsergroupAddOutlined />} />
        </Tooltip>
      );

    return (
      <GroupSearchButton
        title="Change administrator group"
        onSelect={selectNewAdminGroup}
        icon={<UsergroupAddOutlined />}
      />
    );
  };

  const ManageButtonComponent = ({ managementUrlTemplate, item }) => {
    if (item.managementUrl)
      return (
        <Tooltip title="Manage this resource">
          <Button
            icon={<SettingOutlined />}
            href={item.managementUrl}
            target="_blank"
          />
        </Tooltip>
      );

    if (managementUrlTemplate) {
      const href = format(managementUrlTemplate, item);
      return (
        <Tooltip title="Manage this resource">
          <Button icon={<SettingOutlined />} href={href} target="_blank" />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="This resource type has no management URL defined">
        <Button disabled icon={<SettingOutlined />} />
      </Tooltip>
    );
  };

  return (
    <Flex wrap gap="small" justify="flex-end">
      {item.internalState === ResourceStates.actionRequired && (
        <ApproveComponent onApprove={onApprove} item={item} />
      )}
      {item.reassignable ? (
        reassignComponent
      ) : (
        <Tooltip title="This resource cannot be reassigned">
          <Button disabled icon={<UserSwitchOutlined />} />
        </Tooltip>
      )}
      <ChangeAdminGroupComponent item={item} />
      <DeleteButtonComponent onDelete={onDelete} item={item} />
      <ManageButtonComponent
        managementUrlTemplate={managementUrlTemplate}
        item={item}
      />
    </Flex>
  );
};

ResourceActionButtons.propTypes = {
  onDelete: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onEditOwner: PropTypes.func.isRequired,
  onSelectNewAdminGroup: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
  managementUrlTemplate: PropTypes.string,
};

export default ResourceActionButtons;
